/**
 * ET Cart Page — AJAX controller (EcomTraffic · Finex / Impact 7.2.0)
 * Loaded (defer) only on templates/cart.finex.json via the layout/theme.liquid head block.
 *
 * PUBLIC CONTRACT — section builders rely on this
 * ---------------------------------------------------------------------------------------------
 * Page wrapper (layout):   [data-et-cart-page][data-sections-url]   (.et-cart-page--empty toggled from cart.item_count)
 *
 * Live sections (re-rendered after every cart mutation / refresh):
 *   Root element  [data-et-cart-live][data-section-id="{{ section.id }}"]
 *   - ALWAYS output the root (hide it with .et-cart-page-section--hidden) so it can be swapped.
 *   - Only the root node is replaced (not #shopify-section-*); <script> inside it will not run.
 *   - A live section whose HTML comes back null / without the root makes the page reload.
 *
 * Line items (inside a live section):
 *   [data-et-cart-line][data-line-key="{{ item.key }}"]   line root (.is-loading + aria-busy while updating)
 *   [data-et-cart-qty="minus"] / [data-et-cart-qty="plus"]   buttons (step = input step || 1, clamped to min/max)
 *   input[data-et-cart-qty-input]   value="{{ item.quantity }}" (value attribute = server quantity), min="0", optional max/step
 *   [data-et-cart-remove]   button or no-JS link → quantity 0
 *   [data-et-cart-line-error]   hidden + role="alert"; filled with the API description/message
 *                               (optional data-error-text = fallback copy). Input reverts to the server quantity.
 *   +/- and typing are debounced 350ms per line key; remove and Enter commit immediately.
 *
 * Upsells:
 *   form[data-et-cart-upsell-form]   fields name="id", optional name="quantity" (default 1), selling_plan, properties[...]
 *                                    (must also post natively to routes.cart_add_url without JS)
 *   [data-et-cart-upsell-submit]     gets .is-loading + disabled while adding
 *   [data-et-cart-upsell-error]      hidden; looked up in closest [data-et-cart-upsell-card], then the form
 *                                    (optional data-error-text fallback)
 *
 * Discount codes:
 *   form[data-et-cart-discount-form][data-error-text] + input[data-et-cart-discount-input]
 *   [data-et-cart-discount-message]   in the form (or its live section) — receives data-error-text / API message;
 *                                     only its text changes ([hidden] is never set: role="status" stays in the a11y tree)
 *   New codes are added on top of every code already on the cart (applicable or not); only the new code is judged.
 *   [data-et-cart-discount-remove][data-code="CODE"]   removes that code
 *
 * Checkout: a form submitted with button[name="checkout"], a form[data-et-cart-checkout] or an a[data-et-cart-checkout]
 *   waits for pending quantity updates before it navigates (and stops if a line error is visible).
 *
 * Events
 *   document         'et-cart:section-replaced'  detail { sectionId, element }  → re-init sliders / widgets here
 *   documentElement  'cart:change' (bubbles)     detail { baseEvent: 'line-item:change', cart: { ...cart, sections }, etCartPage: true }
 *                    (theme CartCount / CartDrawer / fetchCart stay in sync; 'variant:add' is never dispatched)
 *   Theme sections (cart drawer) are bundled through the theme's 'cart:prepare-bundled-sections' event.
 *   Listens: 'cart:change' / 'cart:refresh' without detail.etCartPage → refresh(); bfcache 'pageshow' → refresh().
 *   Handlers are delegated on document, so nothing needs re-binding on 'shopify:section:load'.
 *
 * window.EtCartPage — every call returns a Promise resolving to the cart object; all run in one serial queue.
 *   refresh()                      GET sections (data-sections-url) + GET cart.js, swap live sections
 *   change(lineKey, quantity)      POST cart/change.js
 *   add(formEl | [{ id, quantity }])  POST cart/add.js, then GET cart.js
 *   applyDiscountCodes(codes[])    POST cart/update.js { discount: codes.join(',') } → cart.discount_codes [{ code, applicable }]
 * The Section Rendering API renders max 5 sections per request; extra ids are fetched in parallel chunks.
 */
(function () {
  'use strict';

  const pageEl = document.querySelector('[data-et-cart-page]');
  if (!pageEl || window.EtCartPage) return;

  const SECTIONS_PER_REQUEST = 5;
  const DEBOUNCE_MS = 350;
  const LIVE_SELECTOR = '[data-et-cart-live][data-section-id]';
  const LINE_SELECTOR = '[data-et-cart-line][data-line-key]';
  const QTY_INPUT_SELECTOR = 'input[data-et-cart-qty-input]';
  const LINE_ERROR_SELECTOR = '[data-et-cart-line-error]';
  const FOCUS_ATTRIBUTES = ['data-et-cart-qty', 'data-et-cart-qty-input', 'data-et-cart-remove'];
  const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';

  const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const sectionsUrl = pageEl.getAttribute('data-sections-url') || window.location.pathname + window.location.search;

  let queueTail = Promise.resolve();
  let pendingTasks = 0;
  let refreshPromise = null;
  let bypassCheckoutGuard = false;
  let replacingSections = false;
  const debounceTimers = {};
  const pendingQuantities = {};
  const draftQuantities = {};
  const lastRendered = {};
  const busyForms = new WeakSet();

  /* ------------------------------------------------------------------ helpers */

  const noop = () => {};
  const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const logError = (error) => window.console && window.console.error('[EtCartPage]', error);
  const reloadPage = (reason) => {
    // Fallback path: say why, so a reload loop is diagnosable instead of silent
    console.warn('[EtCartPage] falling back to a page reload:', reason || 'unknown');
    try { sessionStorage.setItem('etCartPageReload', String(reason || 'unknown')); } catch (error) { /* private mode */ }
    window.location.reload();
  };

  function toInt(value, fallback) {
    const number = parseInt(value, 10);
    return Number.isNaN(number) ? fallback : number;
  }

  function escapeAttribute(value) {
    return window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape(String(value)) : String(value).replace(/["\\]/g, '\\$&');
  }

  const liveSelector = (id) => `[data-et-cart-live][data-section-id="${escapeAttribute(id)}"]`;
  const lineSelector = (key) => `[data-et-cart-line][data-line-key="${escapeAttribute(key)}"]`;
  const findLine = (key) => document.querySelector(lineSelector(key));
  const unique = (list) => list.filter((value, index) => value && list.indexOf(value) === index);

  function closestInPage(target, selector) {
    if (!(target instanceof Element)) return null;
    const match = target.closest(selector);
    return match && pageEl.contains(match) ? match : null;
  }

  function sectionIdOf(element) {
    const section = element && element.closest(LIVE_SELECTOR);
    return section ? section.getAttribute('data-section-id') : '';
  }

  function chunk(list, size) {
    const groups = [];
    for (let index = 0; index < list.length; index += size) groups.push(list.slice(index, index + size));
    return groups;
  }

  function setPageBusy(isBusy) {
    if (isBusy) pageEl.setAttribute('aria-busy', 'true');
    else pageEl.removeAttribute('aria-busy');
  }

  function setLineBusy(key, isBusy) {
    const line = findLine(key);
    if (!line) return;
    line.classList.toggle('is-loading', isBusy);
    if (isBusy) line.setAttribute('aria-busy', 'true');
    else line.removeAttribute('aria-busy');
  }

  function setControlLoading(control, isLoading) {
    if (!control) return;
    control.classList.toggle('is-loading', isLoading);
    if (!('disabled' in control)) return;
    if (isLoading) {
      control.dataset.etCartWasDisabled = control.disabled ? 'true' : 'false';
      control.disabled = true;
    } else {
      control.disabled = control.dataset.etCartWasDisabled === 'true';
      delete control.dataset.etCartWasDisabled;
    }
  }

  const messageTokens = new WeakMap();

  /** The discount role="status" region must stay in the accessibility tree: it is never [hidden], CSS collapses it while empty. */
  const togglesHidden = (target) => !target.hasAttribute('data-et-cart-discount-message');

  /** Live regions: text written in the same turn the region is inserted / un-hidden is often not announced, so fill it two frames later. */
  function showMessage(target, message) {
    if (!target) return;
    const text = message || target.getAttribute('data-error-text') || '';
    const token = {};
    messageTokens.set(target, token);
    target.textContent = '';
    if (togglesHidden(target)) target.hidden = !text;
    if (!text) return;
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        if (messageTokens.get(target) === token && target.isConnected) target.textContent = text;
      })
    );
  }

  function hideMessage(target) {
    if (!target) return;
    messageTokens.delete(target);
    target.textContent = '';
    if (togglesHidden(target)) target.hidden = true;
  }

  /* ------------------------------------------------------------------ queue */

  function enqueue(task) {
    pendingTasks += 1;
    setPageBusy(true);
    const run = queueTail.then(() => task());
    queueTail = run.then(noop, noop).then(() => {
      pendingTasks -= 1;
      if (pendingTasks === 0) setPageBusy(false);
    });
    return run;
  }

  const hasPendingWork = () => pendingTasks > 0 || Object.keys(debounceTimers).length > 0;

  function waitForPendingWork() {
    Object.keys(debounceTimers).forEach(commitQuantity);
    return queueTail.then(() => (hasPendingWork() ? waitForPendingWork() : undefined));
  }

  /* ------------------------------------------------------------------ network */

  async function readJson(response) {
    const text = await response.text();
    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        data = null;
      }
    }
    if (!response.ok || data === null) {
      const error = new Error(`Cart request failed (${response.status})`);
      error.status = response.status;
      error.data = data;
      error.userMessage = '';
      if (data && typeof data.description === 'string' && data.description) error.userMessage = data.description;
      else if (data && typeof data.message === 'string' && data.message) error.userMessage = data.message;
      throw error;
    }
    return data;
  }

  function postJson(path, body) {
    return fetch(routesRoot + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(readJson);
  }

  function getJson(url) {
    return fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }).then(readJson);
  }

  const getCart = () => getJson(`${routesRoot}cart.js`);

  /** Section Rendering API GET. No `Accept: application/json` header: with it, /cart answers with the cart JSON instead of the sections map. */
  async function fetchSections(ids) {
    if (!ids.length) return {};
    const joiner = sectionsUrl.indexOf('?') === -1 ? '?' : '&';
    const results = await Promise.all(
      chunk(ids, SECTIONS_PER_REQUEST).map((group) =>
        fetch(`${sectionsUrl}${joiner}sections=${group.map(encodeURIComponent).join(',')}`, { credentials: 'same-origin', cache: 'no-store' }).then(readJson)
      )
    );
    return Object.assign({}, ...results);
  }

  function liveSectionIds() {
    return unique(Array.from(document.querySelectorAll(LIVE_SELECTOR), (element) => element.getAttribute('data-section-id')));
  }

  /** Same mechanism as the theme's product form / line item quantity: listeners push their section ids. */
  function themeSectionIds() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    return unique(sections.map(String));
  }

  /** POST a cart mutation with bundled sections; returns { cart, sections, liveIds, themeIds } (not yet applied). */
  async function mutate(path, payload, needsCartFetch) {
    const liveIds = liveSectionIds();
    const themeIds = themeSectionIds().filter((id) => liveIds.indexOf(id) === -1);
    const ids = liveIds.concat(themeIds);
    const body = Object.assign({}, payload);

    if (ids.length) {
      body.sections = ids.slice(0, SECTIONS_PER_REQUEST);
      body.sections_url = sectionsUrl;
    }

    const response = await postJson(path, body);
    const sections = Object.assign({}, response.sections || {});
    let cart = null;

    if (needsCartFetch) {
      cart = await getCart().catch((error) => {
        logError(error);
        return null;
      });
    } else {
      cart = Object.assign({}, response);
      delete cart.sections;
    }

    const extra = await fetchSections(ids.slice(SECTIONS_PER_REQUEST)).catch((error) => {
      logError(error);
      return {};
    });

    return { cart, sections: Object.assign(sections, extra), liveIds, themeIds };
  }

  /* ------------------------------------------------------------------ applying results */

  /**
   * A section bundled into a cart POST only counts when it actually came back as this template's section.
   * Sections that live in the TEMPLATE (header, line items, upsells) render without the `finex` suffix in that
   * context — Shopify ignores the ?view= of sections_url there — so they come back as the guard's fallback,
   * without the live root. That is indistinguishable from a broken section unless the content is checked.
   */
  const hasLiveRoot = (html, id) =>
    typeof html === 'string' && html.indexOf('data-et-cart-live') !== -1 && html.indexOf(`data-section-id="${id}"`) !== -1;

  /** Re-fetch every live section the POST did not return usable markup for, with a direct GET (that keeps ?view=). */
  async function completeLiveSections(result) {
    const missing = result.liveIds.filter((id) => !hasLiveRoot(result.sections[id], id));
    if (!missing.length) return result;
    const more = await fetchSections(missing).catch(() => ({}));
    Object.keys(more).forEach((id) => {
      if (typeof more[id] === 'string') result.sections[id] = more[id];
    });
    return result;
  }

  async function finalize(result, syncWithTheme) {
    await completeLiveSections(result);
    if (!result.cart) {
      reloadPage('cart fetch failed');
      return null;
    }
    if (!applySections(result)) return result.cart;
    if (syncWithTheme) dispatchThemeChange(result);
    return result.cart;
  }

  function applySections(result) {
    const entries = [];
    let mustReload = false;

    result.liveIds.forEach((id) => {
      const current = document.querySelector(liveSelector(id));
      if (!current) return;

      const html = result.sections[id];
      if (typeof html !== 'string') {
        mustReload = `section missing from the response: ${id}`;
        return;
      }

      const record = lastRendered[id];
      if (record && record.element === current && record.html === html) return;

      const next = new DOMParser().parseFromString(html, 'text/html').querySelector(liveSelector(id));
      if (!next) {
        mustReload = `section markup has no live root: ${id}`;
        return;
      }
      entries.push({ id, current, next, html });
    });

    if (mustReload) {
      reloadPage(mustReload);
      return false;
    }

    const focusState = captureFocus();
    const previousShipping = freeShippingText();
    // Removing a focused, edited quantity input makes the browser fire 'change' on it before it detaches:
    // that must not commit the typed draft (it is restored onto the new input below).
    let replaced = [];
    replacingSections = true;
    try {
      replaced = entries.map(({ id, current, next, html }) => {
        const element = document.adoptNode(next);
        current.replaceWith(element);
        lastRendered[id] = { html, element };
        return { sectionId: id, element };
      });
    } finally {
      replacingSections = false;
    }

    restorePendingQuantities();
    replaced.forEach((detail) => document.dispatchEvent(new CustomEvent('et-cart:section-replaced', { detail })));
    restoreFocus(focusState);
    announceFreeShipping(previousShipping);
    updateEmptyState(result.cart);
    return true;
  }

  /** Current free-shipping message text ('' when the section is hidden or has no message). */
  function freeShippingText() {
    const message = pageEl.querySelector('.et-cart-free-shipping__message');
    return message ? message.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  /**
   * A live region that is inserted already filled is not reliably announced, and the free-shipping root is replaced on
   * every cart change. So a changed message is written into the layout's persistent #status-announcement region
   * instead, two frames later (same timing as showMessage).
   */
  function announceFreeShipping(previousText) {
    const nextText = freeShippingText();
    const status = document.getElementById('status-announcement');
    if (!status || !nextText || nextText === previousText) return;
    status.textContent = '';
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        status.textContent = nextText;
      })
    );
  }

  /** Never collapses the layout in the theme editor, where the sidebar sections must stay selectable (see layout/theme.liquid). */
  function updateEmptyState(cart) {
    if (cart && typeof cart.item_count === 'number') pageEl.classList.toggle('et-cart-page--empty', cart.item_count === 0 && !designMode);
  }

  /** Keep theme components (cart count, drawer, fetchCart cache) in sync without opening the drawer. */
  function dispatchThemeChange(result) {
    const { sections, themeIds } = result;
    const themeSectionsReady = themeIds.every((id) => typeof sections[id] === 'string');

    if (themeSectionsReady) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart: Object.assign({}, result.cart, { sections }), etCartPage: true }
        })
      );
    } else {
      // The drawer would throw on a missing section: let theme components re-fetch themselves instead.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { etCartPage: true } }));
    }
  }

  /** Re-apply scheduled quantities and not-yet-committed typed values to freshly rendered inputs. */
  function restorePendingQuantities() {
    [pendingQuantities, draftQuantities].forEach((values) => {
      Object.keys(values).forEach((key) => {
        const line = findLine(key);
        const input = line && line.querySelector(QTY_INPUT_SELECTOR);
        if (input) input.value = values[key];
        else if (values === draftQuantities) delete draftQuantities[key];
      });
    });
  }

  /* ------------------------------------------------------------------ focus preservation */

  function captureFocus() {
    const active = document.activeElement;
    if (!active || active === document.body || !pageEl.contains(active)) return null;

    const section = active.closest(LIVE_SELECTOR);
    if (!section) return null;

    const state = { element: active, id: active.id || '', sectionId: section.getAttribute('data-section-id'), lineKey: '', lineIndex: -1, attribute: '', attributeValue: '', selection: null };
    const line = active.closest(LINE_SELECTOR);

    if (line) {
      state.lineKey = line.getAttribute('data-line-key');
      state.lineIndex = Array.prototype.indexOf.call(section.querySelectorAll(LINE_SELECTOR), line);
      FOCUS_ATTRIBUTES.some((name) => {
        const owner = active.closest(`[${name}]`);
        if (!owner || !line.contains(owner)) return false;
        state.attribute = name;
        state.attributeValue = owner.getAttribute(name);
        return true;
      });
    }

    try {
      if (typeof active.selectionStart === 'number') state.selection = [active.selectionStart, active.selectionEnd];
    } catch (selectionError) {
      state.selection = null;
    }
    return state;
  }

  function findLineControl(line, state) {
    if (!line || !state.attribute) return null;
    const selector = state.attributeValue ? `[${state.attribute}="${escapeAttribute(state.attributeValue)}"]` : `[${state.attribute}]`;
    const control = line.querySelector(selector);
    if (control && !control.disabled) return control;
    return line.querySelector(QTY_INPUT_SELECTOR);
  }

  function restoreFocus(state) {
    if (!state || state.element.isConnected) return;

    let target = null;
    if (state.lineKey) {
      target = findLineControl(findLine(state.lineKey), state);
      if (!target) {
        // The line was removed: move to the same control on the neighbouring line.
        const section = document.querySelector(liveSelector(state.sectionId));
        const lines = section ? section.querySelectorAll(LINE_SELECTOR) : [];
        if (lines.length) target = findLineControl(lines[Math.min(Math.max(state.lineIndex, 0), lines.length - 1)], state);
      }
    }
    if (!target && state.id) {
      const byId = document.getElementById(state.id);
      if (byId && pageEl.contains(byId)) target = byId;
    }
    if (!target) target = document.querySelector(liveSelector(state.sectionId));
    if (!target || target.getClientRects().length === 0) return;

    if (!target.matches(FOCUSABLE_SELECTOR)) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });

    if (state.selection && document.activeElement === target && typeof target.setSelectionRange === 'function') {
      try {
        target.setSelectionRange(state.selection[0], state.selection[1]);
      } catch (selectionError) {
        /* input type does not support selection */
      }
    }
  }

  /* ------------------------------------------------------------------ line items */

  const lineInput = (line) => (line ? line.querySelector(QTY_INPUT_SELECTOR) : null);
  const serverQuantity = (input) => (input ? toInt(input.defaultValue, 0) : 0);

  function clampQuantity(input, value) {
    const min = input ? Math.max(0, toInt(input.min, 0)) : 0;
    const max = input ? toInt(input.max, Infinity) : Infinity;
    return Math.max(min, Math.min(max, value));
  }

  function scheduleQuantity(key, quantity, immediate) {
    const line = findLine(key);
    const input = lineInput(line);
    const baseline = hasOwn(pendingQuantities, key) ? pendingQuantities[key] : serverQuantity(input);
    delete draftQuantities[key];

    if (!debounceTimers[key] && baseline === quantity) {
      if (input && toInt(input.value, NaN) !== quantity) input.value = quantity;
      return;
    }

    clearTimeout(debounceTimers[key]);
    delete debounceTimers[key];
    pendingQuantities[key] = quantity;
    hideMessage(line && line.querySelector(LINE_ERROR_SELECTOR));
    if (input && toInt(input.value, NaN) !== quantity) input.value = quantity;

    if (immediate) commitQuantity(key);
    else debounceTimers[key] = setTimeout(() => commitQuantity(key), DEBOUNCE_MS);
  }

  function commitQuantity(key) {
    clearTimeout(debounceTimers[key]);
    delete debounceTimers[key];
    if (!hasOwn(pendingQuantities, key)) return;

    const quantity = pendingQuantities[key];
    enqueue(() => performChange(key, quantity)).catch((error) => handleLineError(key, quantity, error));
  }

  async function performChange(key, quantity) {
    setLineBusy(key, true);
    try {
      const result = await mutate('cart/change.js', { id: key, quantity }, false);
      if (!debounceTimers[key] && pendingQuantities[key] === quantity) delete pendingQuantities[key];
      return await finalize(result, true);
    } finally {
      setLineBusy(key, false);
    }
  }

  function handleLineError(key, quantity, error) {
    logError(error);
    if (!debounceTimers[key] && pendingQuantities[key] === quantity) delete pendingQuantities[key];

    const line = findLine(key);
    const input = lineInput(line);
    if (input && !hasOwn(pendingQuantities, key)) input.value = serverQuantity(input);

    const message = (error && error.userMessage) || '';
    if (error && error.status === 422 && line) {
      showMessage(line.querySelector(LINE_ERROR_SELECTOR), message);
      return;
    }

    // Unknown state (stale line key, network, server error): re-sync, then surface the message if any.
    refresh()
      .then(() => {
        const freshLine = findLine(key);
        if (freshLine && (message || freshLine.querySelector(`${LINE_ERROR_SELECTOR}[data-error-text]`))) {
          showMessage(freshLine.querySelector(LINE_ERROR_SELECTOR), message);
        }
      })
      .catch(logError);
  }

  function onQuantityButton(event, button) {
    event.preventDefault();
    const line = button.closest(LINE_SELECTOR);
    if (!line || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const key = line.getAttribute('data-line-key');
    const input = lineInput(line);
    const current = hasOwn(pendingQuantities, key) ? pendingQuantities[key] : toInt(input && input.value, serverQuantity(input));
    const step = Math.max(1, toInt(input && input.step, 1));
    const direction = button.getAttribute('data-et-cart-qty') === 'minus' ? -1 : 1;
    const next = clampQuantity(input, current + direction * step);

    if (next !== current) scheduleQuantity(key, next, false);
  }

  function onRemove(event, control) {
    const line = control.closest(LINE_SELECTOR);
    if (!line) return;
    event.preventDefault();
    scheduleQuantity(line.getAttribute('data-line-key'), 0, true);
  }

  function onQuantityInput(event) {
    if (replacingSections) return;
    const input = closestInPage(event.target, QTY_INPUT_SELECTOR);
    const line = input && input.closest(LINE_SELECTOR);
    if (!line) return;

    const key = line.getAttribute('data-line-key');

    // Typed values commit on change (blur) or Enter only. Committing per keystroke would send intermediate
    // values (typing "0" on the way to "10" removes the line). The draft survives a re-render of the section.
    if (event.type === 'input') {
      draftQuantities[key] = input.value;
      return;
    }
    delete draftQuantities[key];

    const value = toInt(input.value, NaN);
    if (Number.isNaN(value)) {
      input.value = hasOwn(pendingQuantities, key) ? pendingQuantities[key] : serverQuantity(input);
      return;
    }

    const next = clampQuantity(input, value);
    scheduleQuantity(key, next, event.type === 'keydown');
  }

  /** A draft restored onto a re-rendered input was set programmatically, so blur fires no 'change': commit it here. */
  function onQuantityFocusOut(event) {
    const input = closestInPage(event.target, QTY_INPUT_SELECTOR);
    const line = input && input.closest(LINE_SELECTOR);
    if (line && hasOwn(draftQuantities, line.getAttribute('data-line-key'))) onQuantityInput(event);
  }

  function onKeydown(event) {
    if (event.key !== 'Enter' || event.isComposing) return;
    if (!closestInPage(event.target, QTY_INPUT_SELECTOR)) return;
    event.preventDefault();
    onQuantityInput(event);
  }

  /* ------------------------------------------------------------------ upsells */

  function itemsFromForm(form) {
    const data = new FormData(form);
    const id = toInt(data.get('id'), NaN);
    if (Number.isNaN(id)) return [];

    const item = { id, quantity: Math.max(1, toInt(data.get('quantity'), 1)) };
    const sellingPlan = toInt(data.get('selling_plan'), NaN);
    if (!Number.isNaN(sellingPlan)) item.selling_plan = sellingPlan;

    const properties = {};
    data.forEach((value, name) => {
      const match = /^properties\[(.+)\]$/.exec(name);
      if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
    });
    if (Object.keys(properties).length) item.properties = properties;
    return [item];
  }

  function upsellErrorTarget(form) {
    const card = form.closest('[data-et-cart-upsell-card]');
    return (card && card.querySelector('[data-et-cart-upsell-error]')) || form.querySelector('[data-et-cart-upsell-error]');
  }

  function onUpsellSubmit(form, submitter) {
    if (busyForms.has(form)) return;
    const button = submitter && submitter.hasAttribute('data-et-cart-upsell-submit') ? submitter : form.querySelector('[data-et-cart-upsell-submit]');

    hideMessage(upsellErrorTarget(form));
    busyForms.add(form);
    setControlLoading(button, true);

    add(form)
      .catch((error) => {
        logError(error);
        if (form.isConnected) showMessage(upsellErrorTarget(form), error && error.userMessage);
      })
      .then(() => {
        busyForms.delete(form);
        if (button && button.isConnected) setControlLoading(button, false);
      });
  }

  /* ------------------------------------------------------------------ discount codes */

  const sameCode = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

  /** Every code on the cart, applicable or not (e.g. below its minimum spend), so re-sending the list never drops one. */
  function cartCodes(cart) {
    return ((cart && cart.discount_codes) || []).map((entry) => entry && entry.code).filter((code) => typeof code === 'string' && code);
  }

  function normaliseCodes(codes) {
    const list = [];
    (Array.isArray(codes) ? codes : [codes]).forEach((code) => {
      const value = typeof code === 'string' ? code.trim() : '';
      if (value && !list.some((existing) => sameCode(existing, value))) list.push(value);
    });
    return list;
  }

  const updateDiscounts = (codes) => mutate('cart/update.js', { discount: normaliseCodes(codes).join(',') }, false);

  function discountForm(form, sectionId) {
    if (form && form.isConnected) return form;
    const section = sectionId ? document.querySelector(liveSelector(sectionId)) : null;
    return (section && section.querySelector('form[data-et-cart-discount-form]')) || pageEl.querySelector('form[data-et-cart-discount-form]');
  }

  function discountMessageTarget(form, sectionId) {
    const inForm = form && form.querySelector('[data-et-cart-discount-message]');
    if (inForm) return inForm;
    const section = sectionId ? document.querySelector(liveSelector(sectionId)) : null;
    return section ? section.querySelector('[data-et-cart-discount-message]') : null;
  }

  function onDiscountSubmit(form, submitter) {
    if (busyForms.has(form)) return;

    const input = form.querySelector('[data-et-cart-discount-input]');
    const code = input ? input.value.trim() : '';
    const sectionId = sectionIdOf(form);

    hideMessage(discountMessageTarget(form, sectionId));
    if (!code) {
      if (input) input.focus();
      return;
    }

    const button = submitter || form.querySelector('button[type="submit"], button:not([type]), input[type="submit"]');
    busyForms.add(form);
    setControlLoading(button, true);

    enqueue(async () => {
      const cart = await getCart();
      const current = ((cart && cart.discount_codes) || []).find((entry) => entry && sameCode(entry.code, code));
      if (current) return { rejected: current.applicable === false };

      // Only the newly added code is judged; the customer's other codes are re-sent as they are.
      const existing = cartCodes(cart);
      const result = await updateDiscounts(existing.concat(code));
      const entry = ((result.cart && result.cart.discount_codes) || []).find((candidate) => candidate && sameCode(candidate.code, code));

      if (entry && entry.applicable === false) {
        await finalize(await updateDiscounts(existing), true);
        return { rejected: true };
      }
      await finalize(result, true);
      return { rejected: false };
    })
      .then(
        (outcome) => {
          if (!outcome.rejected) return;
          const currentForm = discountForm(form, sectionId);
          if (!currentForm) return;
          showMessage(discountMessageTarget(currentForm, sectionId), currentForm.getAttribute('data-error-text'));
          const currentInput = currentForm.querySelector('[data-et-cart-discount-input]');
          if (currentInput) {
            currentInput.value = code;
            currentInput.focus({ preventScroll: true });
          }
        },
        (error) => {
          logError(error);
          const currentForm = discountForm(form, sectionId);
          if (currentForm) showMessage(discountMessageTarget(currentForm, sectionId), (error && error.userMessage) || currentForm.getAttribute('data-error-text'));
        }
      )
      .then(() => {
        busyForms.delete(form);
        if (button && button.isConnected) setControlLoading(button, false);
      });
  }

  function onDiscountRemove(event, control) {
    event.preventDefault();
    const code = control.getAttribute('data-code');
    if (!code || control.classList.contains('is-loading')) return;

    const sectionId = sectionIdOf(control);
    setControlLoading(control, true);

    enqueue(async () => {
      const cart = await getCart();
      const remaining = cartCodes(cart).filter((applied) => !sameCode(applied, code));
      return finalize(await updateDiscounts(remaining), true);
    })
      .catch((error) => {
        logError(error);
        const currentForm = discountForm(null, sectionId);
        const target = discountMessageTarget(currentForm, sectionId);
        if (target) showMessage(target, (error && error.userMessage) || (currentForm && currentForm.getAttribute('data-error-text')));
      })
      .then(() => {
        if (control.isConnected) setControlLoading(control, false);
      });
  }

  /* ------------------------------------------------------------------ checkout guard */

  const hasVisibleLineError = () => Boolean(pageEl.querySelector(`${LINE_ERROR_SELECTOR}:not([hidden])`));

  /** Checkout waits for pending quantity/discount requests: mark the control busy so the click is visibly doing something. */
  function setCheckoutBusy(control, busy) {
    if (!control || !control.isConnected) return;
    if (busy) control.setAttribute('aria-busy', 'true');
    else control.removeAttribute('aria-busy');
  }

  function onCheckoutSubmit(event, form) {
    const submitter = event.submitter || null;
    const isCheckout = form.hasAttribute('data-et-cart-checkout') || (submitter && submitter.name === 'checkout');
    if (!isCheckout || bypassCheckoutGuard || !hasPendingWork()) return;

    event.preventDefault();
    const sectionId = sectionIdOf(form);
    const section = sectionId ? document.querySelector(liveSelector(sectionId)) : null;
    const formIndex = section ? Array.prototype.indexOf.call(section.querySelectorAll('form'), form) : -1;
    const busyControl = submitter || form.querySelector('[name="checkout"]');
    setCheckoutBusy(busyControl, true);

    waitForPendingWork().then(() => {
      setCheckoutBusy(busyControl, false);
      if (hasVisibleLineError()) return;

      let nextForm = form;
      if (!form.isConnected) {
        const nextSection = sectionId ? document.querySelector(liveSelector(sectionId)) : null;
        nextForm = nextSection && formIndex > -1 ? nextSection.querySelectorAll('form')[formIndex] : null;
      }
      if (!nextForm || nextForm.getClientRects().length === 0) return;

      let nextSubmitter = submitter && submitter.isConnected && submitter.form === nextForm ? submitter : null;
      if (!nextSubmitter && submitter && submitter.name === 'checkout') nextSubmitter = nextForm.querySelector('[name="checkout"]');

      bypassCheckoutGuard = true;
      try {
        if (typeof nextForm.requestSubmit === 'function') {
          if (nextSubmitter) nextForm.requestSubmit(nextSubmitter);
          else nextForm.requestSubmit();
        } else {
          if (nextSubmitter && nextSubmitter.name) {
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = nextSubmitter.name;
            hidden.value = nextSubmitter.value;
            nextForm.appendChild(hidden);
          }
          nextForm.submit();
        }
      } finally {
        bypassCheckoutGuard = false;
      }
    });
  }

  function onCheckoutLink(event, link) {
    if (bypassCheckoutGuard || !hasPendingWork() || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    const href = link.href;
    setCheckoutBusy(link, true);
    waitForPendingWork().then(() => {
      setCheckoutBusy(link, false);
      if (!hasVisibleLineError()) window.location.assign(href);
    });
  }

  /* ------------------------------------------------------------------ delegated listeners */

  function onClick(event) {
    const target = event.target;

    const quantityButton = closestInPage(target, '[data-et-cart-qty]');
    if (quantityButton) return onQuantityButton(event, quantityButton);

    const removeControl = closestInPage(target, '[data-et-cart-remove]');
    if (removeControl) return onRemove(event, removeControl);

    const discountRemove = closestInPage(target, '[data-et-cart-discount-remove][data-code]');
    if (discountRemove) return onDiscountRemove(event, discountRemove);

    const checkoutLink = closestInPage(target, 'a[href][data-et-cart-checkout]');
    if (checkoutLink) return onCheckoutLink(event, checkoutLink);

    return undefined;
  }

  function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !pageEl.contains(form)) return;

    if (form.hasAttribute('data-et-cart-upsell-form')) {
      event.preventDefault();
      onUpsellSubmit(form, event.submitter || null);
    } else if (form.hasAttribute('data-et-cart-discount-form')) {
      event.preventDefault();
      onDiscountSubmit(form, event.submitter || null);
    } else {
      onCheckoutSubmit(event, form);
    }
  }

  function onExternalCartEvent(event) {
    if (event.detail && event.detail.etCartPage) return;
    refresh().catch(logError);
  }

  /* ------------------------------------------------------------------ sticky sidebar */

  /** The sidebar only sticks while it fits below the sticky header, so a tall sidebar is never clipped (CSS: .et-cart-page__aside--sticky). */
  function initStickyAside() {
    const aside = pageEl.querySelector('.et-cart-page__aside');
    if (!aside || pageEl.dataset.stickyAside === 'false') return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const stickyTop = parseFloat(window.getComputedStyle(aside).top) || 0;
      // offsetHeight is the content alone (the 48/64 column spacing is margin), which is what sticks.
      const fits = aside.offsetHeight > 0 && aside.offsetHeight <= window.innerHeight - stickyTop;
      aside.classList.toggle('et-cart-page__aside--sticky', fits);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    // Toggling position: sticky never changes the sidebar's height, so this cannot loop.
    if (typeof window.ResizeObserver === 'function') new window.ResizeObserver(schedule).observe(aside);
    window.addEventListener('resize', schedule);
    document.addEventListener('et-cart:section-replaced', schedule);
    document.addEventListener('shopify:section:load', schedule);
    schedule();
  }

  /* ------------------------------------------------------------------ public API */

  function refresh() {
    if (refreshPromise) return refreshPromise;

    const promise = enqueue(async () => {
      if (refreshPromise === promise) refreshPromise = null;
      const liveIds = liveSectionIds();
      const [cart, sections] = await Promise.all([getCart(), fetchSections(liveIds)]);
      return finalize({ cart, sections, liveIds, themeIds: [] }, false);
    });

    refreshPromise = promise;
    promise.catch(() => {
      if (refreshPromise === promise) refreshPromise = null;
    });
    return promise;
  }

  function change(lineKey, quantity) {
    return enqueue(() => performChange(String(lineKey), Math.max(0, toInt(quantity, 0))));
  }

  function add(source) {
    const items = Array.isArray(source) ? source : source instanceof HTMLFormElement ? itemsFromForm(source) : [];
    if (!items.length) return Promise.reject(new Error('EtCartPage.add: no variant id'));
    return enqueue(async () => finalize(await mutate('cart/add.js', { items }, true), true));
  }

  function applyDiscountCodes(codes) {
    return enqueue(async () => finalize(await updateDiscounts(codes), true));
  }

  document.addEventListener('click', onClick);
  document.addEventListener('input', onQuantityInput);
  document.addEventListener('change', onQuantityInput);
  document.addEventListener('focusout', onQuantityFocusOut);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('submit', onSubmit);
  document.addEventListener('cart:change', onExternalCartEvent);
  document.addEventListener('cart:refresh', onExternalCartEvent);
  document.addEventListener('shopify:section:load', restorePendingQuantities);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh().catch(logError);
  });
  initStickyAside();

  window.EtCartPage = { refresh, change, add, applyDiscountCodes };
})();
